import { AppBaseUrl } from "../utils/http";

export const CATALOG_PATH_PREFIX = "/catalogue";

/** Valeur enregistrée sur Order.canale_acquisition pour les ventes catalogue. */
export const CATALOG_ACQUISITION_CHANNEL = "Catalogue";

export type CatalogVendeurPublic = {
  slug: string;
  catalog_slug?: string;
  nom_boutique: string;
  nom_de_la_boutique?: string;
  devise: string;
  is_active: boolean;
  couleur?: string;
  domaine?: string;
  catalog_url?: string | null;
  avatar?: string | null;
  wave_is_set?: boolean;
  stripe_is_set?: boolean;
  paygate_is_set?: boolean;
  bictorys_is_set?: boolean;
  bictorys_public_key?: string | null;
  tva_enabled?: boolean;
  tva_rate?: number | string;
  tva_calculation_mode?: "HT" | "TTC";
};

let activeShopSlug: string | null = null;
let activeVendeur: CatalogVendeurPublic | null = null;

const CATALOG_SLUG_STORAGE_KEY = "catalog_shop_slug";

function extractCatalogSlugFromPath(path: string): string | null {
  const match =
    path.match(/^\/catalogue\/([^/]+)/) || path.match(/^\/b\/([^/]+)/);
  if (!match?.[1]) return null;
  const slug = decodeURIComponent(match[1]).trim();
  return slug || null;
}

export function setCatalogTenant(
  shopSlug: string | null,
  vendeur?: CatalogVendeurPublic | null,
) {
  activeShopSlug = shopSlug;
  activeVendeur = vendeur ?? null;
  try {
    if (shopSlug) {
      sessionStorage.setItem(CATALOG_SLUG_STORAGE_KEY, shopSlug);
    } else {
      sessionStorage.removeItem(CATALOG_SLUG_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function getCatalogShopSlug(): string | null {
  if (activeShopSlug) return activeShopSlug;
  try {
    return sessionStorage.getItem(CATALOG_SLUG_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getCatalogVendeur(): CatalogVendeurPublic | null {
  return activeVendeur;
}

/** Extrait le slug catalogue depuis l'URL et initialise le tenant avant les appels API. */
export function syncCatalogSlugFromPathname(pathname?: string): string | null {
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  const slug = extractCatalogSlugFromPath(path);
  if (!slug) return null;

  if (slug !== activeShopSlug) {
    setCatalogTenant(slug, activeVendeur?.slug === slug ? activeVendeur : null);
  }
  return slug;
}

/** Hôte front catalogue (ex: catalogue.easymarket.sn). */
export function isCatalogHost(url: string = AppBaseUrl): boolean {
  return String(url || "")
    .trim()
    .toLowerCase()
    .includes("catalogue.");
}

export function isCatalogPath(pathname?: string): boolean {
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  return /^\/catalogue\/[^/]+/.test(path) || /^\/b\/[^/]+/.test(path);
}

/** Contexte commande catalogue : URL, slug mémorisé, ou hôte catalogue. */
export function isCatalogOrderContext(pathname?: string): boolean {
  syncCatalogSlugFromPathname(pathname);
  return (
    isCatalogPath(pathname) ||
    !!getCatalogShopSlug() ||
    isCatalogHost()
  );
}

export function buildCatalogPath(subPath = ""): string {
  const slug =
    getCatalogVendeur()?.catalog_slug || getCatalogShopSlug();
  if (!slug) {
    return subPath.startsWith("/") ? subPath : `/${subPath}`;
  }
  const normalized = subPath
    ? subPath.startsWith("/")
      ? subPath
      : `/${subPath}`
    : "";
  return `${CATALOG_PATH_PREFIX}/${slug}${normalized}`;
}

export function buildPublicCatalogUrl(shopSlug: string): string {
  const vendeur = getCatalogVendeur();
  if (vendeur?.catalog_url) {
    return vendeur.catalog_url;
  }
  const slug = vendeur?.catalog_slug || shopSlug;
  return `${AppBaseUrl.replace(/\/$/, "")}${CATALOG_PATH_PREFIX}/${slug}`;
}

/** Texte de partage catalogue (WhatsApp, copie presse-papiers). */
export function buildCatalogShareMessage(
  shopName: string,
  catalogUrl: string,
): string {
  const name = shopName?.trim() || "Ma boutique";
  return `🛍️ ${name}\nDécouvrez notre catalogue en ligne et commandez en quelques clics.\n\n${catalogUrl}`;
}

export function buildWhatsAppShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

/** Catalogue multi-vendeur : shop-slug uniquement (pas de clé API legacy). */
export function applyCatalogHeaders(headers: Headers): Headers {
  syncCatalogSlugFromPathname();
  const shopSlug = getCatalogShopSlug();
  if (shopSlug) {
    headers.set("shop-slug", shopSlug);
  }
  return headers;
}
